import React from "react";
import { Input } from "../ui/input";

/**
 * Editable quantity cell.
 *
 * Keeps its own local string state so the user can freely select / backspace
 * / retype without the controlled value snapping back mid-edit. Commits to
 * the parent on Enter or blur; reverts on Escape; ignores invalid input.
 *
 * Used by both `SummaryTab` (per-room total qty) and `AssignmentTab`
 * (per-variant split qty).
 */
// `max` is now OPTIONAL — when omitted, the engineer can type any positive
// integer. The only caller that still passes `max` is AssignmentTab's split
// editor, where a sub-variant qty can't exceed the room's total qty (that
// cap is semantically required, not a UX choice). Per Vladimir 2026-06-25:
// large auditorium / atrium projects need >99 units per room.
export function QtyInput({ value, onCommit, max, testid, className }) {
  const [draft, setDraft] = React.useState(String(value));
  React.useEffect(() => { setDraft(String(value)); }, [value]);
  const commit = () => {
    const v = parseInt(draft, 10);
    if (!isNaN(v) && v >= 0 && v !== value) {
      onCommit(max != null ? Math.min(max, v) : v);
    } else if (isNaN(v) || v < 0) {
      setDraft(String(value));
    }
  };
  return (
    <Input
      type="number"
      min={0}
      {...(max != null ? { max } : {})}
      value={draft}
      onChange={(ev) => setDraft(ev.target.value)}
      onBlur={commit}
      onKeyDown={(ev) => {
        if (ev.key === "Enter") { ev.currentTarget.blur(); }
        if (ev.key === "Escape") { setDraft(String(value)); ev.currentTarget.blur(); }
      }}
      onClick={(ev) => ev.stopPropagation()}
      className={className || "h-7 w-14 text-xs text-center px-1"}
      data-testid={testid}
      title="Edit quantity (commits on Enter / blur)"
    />
  );
}

export default QtyInput;
